import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, query, onSnapshot, orderBy, addDoc, doc, getDoc } from './firestore-compat';
import './App.css'; 

const TUTORIAIS_DESATIVADO = true;

function TutoriaisPublico() {
  const [userData, setUserData] = useState(null);
  const [isWelcomeLoading, setIsWelcomeLoading] = useState(false);
  
  // --- FORMULÁRIO ---
  const [form, setForm] = useState({ 
    nomeUsuario: '', 
    especialidade: '', 
    cooperativa: false, 
    qualCooperativa: '', 
    telefone: '', 
    email: '' 
  });
  
  const [especialidades, setEspecialidades] = useState([]);
  const [cooperativas, setCooperativas] = useState([]);
  
  // --- VÍDEOS E COMENTÁRIOS ---
  const [videos, setVideos] = useState([]);
  const [videoAberto, setVideoAberto] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [novoComentario, setNovoComentario] = useState('');

  useEffect(() => {
    if (TUTORIAIS_DESATIVADO) return;
    // 1. Verifica se está logado no sistema (usuário interno)
    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (user) {
        setUserData({
          nomeUsuario: user.displayName || user.email.split('@')[0],
          especialidade: 'Público Geral',
          isInternal: true,
          telefone: '-',
          email: user.email
        });
      } else {
        // Se não, verifica se já preencheu o formulário antes
        const savedData = localStorage.getItem('tutoriais_user_data');
        if (savedData) {
          setUserData(JSON.parse(savedData));
        }
      }
    });

    // 2. Carrega especialidades do config estático
    const loadConfig = async () => {
      const docRef = doc(db, 'configuracoes', 'tutoriais');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setEspecialidades(docSnap.data().especialidades || []);
      }
    };
    loadConfig();

    // 3. Carrega cooperativas dinamicamente da coleção real
    const unsubCoops = onSnapshot(
      query(collection(db, 'cooperativas_config'), orderBy('name')),
      snap => setCooperativas(snap.docs.map(d => d.data().name).filter(Boolean))
    );

    return () => { unsubscribeAuth(); unsubCoops(); };
  }, []);

  // Efeito separado para carregar vídeos que depende do userData
  useEffect(() => {
    if (TUTORIAIS_DESATIVADO) return;
    const unsubVideos = onSnapshot(query(collection(db, 'tutoriais'), orderBy('criadoEm', 'desc')), snap => {
      const vids = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(v => {
        if (!v.ativo) return false;
        
        // Se for interno, tem passe livre para ver tudo
        if (userData && userData.isInternal) return true;

        // Se já temos userData e o vídeo tem especialidades vinculadas
        if (userData && userData.especialidade && v.especialidadesVideo && v.especialidadesVideo.length > 0) {
          return v.especialidadesVideo.includes(userData.especialidade);
        }
        
        return true; 
      });
      setVideos(vids);
    });

    return () => unsubVideos();
  }, [userData]);

  // Carrega comentários do vídeo aberto
  useEffect(() => {
    if (!videoAberto) {
      setComentarios([]);
      return;
    }
    const q = query(collection(db, 'tutoriais', videoAberto.id, 'comentarios'), orderBy('criadoEm', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setComentarios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [videoAberto]);

  const handleAcessar = (e) => {
    e.preventDefault();
    if (!form.nomeUsuario || !form.especialidade || !form.telefone || !form.email) {
      alert("Preencha todos os campos obrigatórios.");
      return;
    }
    
    // Inicia a animação de carregamento
    setIsWelcomeLoading(true);

    // Aguarda a animação antes de liberar o acesso
    setTimeout(() => {
      // Salva localmente
      localStorage.setItem('tutoriais_user_data', JSON.stringify(form));
      setUserData(form);
      setIsWelcomeLoading(false);
    }, 4500); // 4.5 segundos de animação
  };

  const handleSair = () => {
    localStorage.removeItem('tutoriais_user_data');
    setUserData(null);
    setVideoAberto(null);
  };

  const assistVideo = async (video) => {
    setVideoAberto(video);
    
    // Registrar visualização
    try {
      await addDoc(collection(db, 'tutoriais_visualizacoes'), {
        ...userData,
        tutorialId: video.id,
        tutorialTitulo: video.titulo,
        dataVisualizacao: new Date()
      });
    } catch(err) {
      console.error("Erro ao registrar view", err);
    }
  };

  const handleAddComentario = async (e) => {
    e.preventDefault();
    if (!novoComentario.trim() || !videoAberto) return;
    
    try {
      await addDoc(collection(db, 'tutoriais', videoAberto.id, 'comentarios'), {
        texto: novoComentario,
        autor: userData.nomeUsuario,
        criadoEm: new Date()
      });
      setNovoComentario('');
    } catch(err) {
      console.error("Erro ao adicionar comentário:", err);
      alert("Erro ao enviar comentário. Verifique sua conexão e tente novamente.");
    }
  };

  // Helper para formatar o iframe embed (Ex: YouTube ou Drive)
  const renderEmbed = (url) => {
    let embedUrl = url;
    
    // YouTube
    if (url.includes('youtube.com/watch')) {
      const videoId = new URL(url).searchParams.get('v');
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1];
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } 
    // Google Drive
    else if (url.includes('drive.google.com/file/d/')) {
      const parts = url.split('/');
      const idIndex = parts.indexOf('d') + 1;
      const fileId = parts[idIndex];
      embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
    }

    return (
      <iframe 
        src={embedUrl} 
        width="100%" 
        height="500px" 
        style={{ border: 'none', borderRadius: '8px' }}
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
        title="Video Player"
      ></iframe>
    );
  };

  const inputStyle = { padding: '12px', borderRadius: '6px', border: '1px solid #ccc', width: '100%', marginBottom: '16px', boxSizing: 'border-box', fontSize: '15px' };
  const btnStyle = { width: '100%', padding: '14px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' };

  if (TUTORIAIS_DESATIVADO) {
    return (
      <div style={{ padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
        <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Aulas e Tutoriais desativado</h3>
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Este módulo não está mais em uso.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#f4f7f6', minHeight: '100vh', padding: '40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      {isWelcomeLoading ? (
        <div style={{ 
          position: 'fixed', inset: 0, zIndex: 9999, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeOutContainer 4.5s forwards'
        }}>
          <style>
            {`
              @keyframes shake {
                0% { transform: translate(1px, 1px) rotate(0deg); }
                10% { transform: translate(-1px, -2px) rotate(-1deg); }
                20% { transform: translate(-3px, 0px) rotate(1deg); }
                30% { transform: translate(3px, 2px) rotate(0deg); }
                40% { transform: translate(1px, -1px) rotate(1deg); }
                50% { transform: translate(-1px, 2px) rotate(-1deg); }
                60% { transform: translate(-3px, 1px) rotate(0deg); }
                70% { transform: translate(3px, 1px) rotate(-1deg); }
                80% { transform: translate(-1px, -1px) rotate(1deg); }
                90% { transform: translate(1px, 2px) rotate(0deg); }
                100% { transform: translate(1px, -2px) rotate(-1deg); }
              }
              @keyframes blastOff {
                0%, 75% { transform: translateY(0); }
                80% { transform: translateY(30px); } /* Foguete agacha para pegar impulso */
                100% { transform: translateY(-1500px); } /* Decola saindo da tela */
              }
              @keyframes fire {
                0%, 75% { opacity: 0; transform: scaleY(0.5); }
                80% { opacity: 1; transform: scaleY(1); }
                100% { opacity: 1; transform: scaleY(3); }
              }
              @keyframes fadeOutContainer {
                0%, 85% { opacity: 1; }
                100% { opacity: 0; }
              }
              @keyframes pulseText {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.05); opacity: 0.8; }
              }
            `}
          </style>
          
          <div style={{ animation: 'blastOff 4.5s ease-in forwards', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '120px', animation: 'shake 0.4s infinite', position: 'relative', zIndex: 2 }}>🚀</div>
            <div style={{ 
              fontSize: '60px', 
              marginTop: '-40px', 
              animation: 'fire 4.5s ease-in forwards',
              position: 'relative',
              zIndex: 1
            }}>🔥</div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '40px', padding: '0 20px', animation: 'pulseText 2s infinite' }}>
            <h2 style={{ color: '#fff', fontSize: '32px', marginBottom: '20px', lineHeight: '1.4', textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
              Ficamos felizes por você fazer parte da trajetória dos nossos pacientes!
            </h2>
            <p style={{ color: '#e0e7ff', fontSize: '20px', lineHeight: '1.6', maxWidth: '800px', margin: '0 auto', fontWeight: '500' }}>
              A equipe <strong>Homebaby e Vitalcare</strong> agradece o seu companheirismo.<br /><br />
              <span style={{ fontSize: '16px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '2px' }}>Preparando para o lançamento...</span>
            </p>
          </div>
        </div>
      ) : !userData ? (
        <div style={{ maxWidth: '500px', margin: '0 auto', background: '#fff', padding: '40px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h2 style={{ color: '#2c3e50', margin: 0, fontSize: '24px' }}>Acesso aos Treinamentos</h2>
            <p style={{ color: '#7f8c8d', marginTop: '10px' }}>Identifique-se para acessar os vídeos disponíveis.</p>
          </div>

          <form onSubmit={handleAcessar}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#34495e', fontWeight: '600' }}>Nome Completo *</label>
            <input style={inputStyle} value={form.nomeUsuario} onChange={e => setForm({...form, nomeUsuario: e.target.value})} required />

            <label style={{ display: 'block', marginBottom: '6px', color: '#34495e', fontWeight: '600' }}>Especialidade *</label>
            <select style={inputStyle} value={form.especialidade} onChange={e => setForm({...form, especialidade: e.target.value})} required>
              <option value="">Selecione...</option>
              {especialidades.map((esp, i) => <option key={i} value={esp}>{esp}</option>)}
            </select>

            <label style={{ display: 'block', marginBottom: '6px', color: '#34495e', fontWeight: '600' }}>Cooperativa?</label>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="radio" checked={form.cooperativa === true} onChange={() => setForm({...form, cooperativa: true})} /> Sim
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="radio" checked={form.cooperativa === false} onChange={() => setForm({...form, cooperativa: false, qualCooperativa: ''})} /> Não
              </label>
            </div>

            {form.cooperativa && (
              <>
                <label style={{ display: 'block', marginBottom: '6px', color: '#34495e', fontWeight: '600' }}>Qual cooperativa?</label>
                <select style={inputStyle} value={form.qualCooperativa} onChange={e => setForm({...form, qualCooperativa: e.target.value})} required>
                  <option value="">Selecione...</option>
                  {cooperativas.map((coop, i) => <option key={i} value={coop}>{coop}</option>)}
                </select>
              </>
            )}

            <label style={{ display: 'block', marginBottom: '6px', color: '#34495e', fontWeight: '600' }}>Telefone (WhatsApp) *</label>
            <input style={inputStyle} type="tel" value={form.telefone} onChange={e => setForm({...form, telefone: e.target.value})} required />

            <label style={{ display: 'block', marginBottom: '6px', color: '#34495e', fontWeight: '600' }}>E-mail *</label>
            <input style={inputStyle} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />

            <button type="submit" style={btnStyle}>Acessar Aulas</button>
          </form>
        </div>
      ) : (
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
            <div>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>Olá, {userData.nomeUsuario.split(' ')[0]}</h2>
              <p style={{ margin: '5px 0 0', color: '#7f8c8d' }}>Escolha um tutorial para assistir.</p>
            </div>
            {!userData.isInternal && (
              <button style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }} onClick={handleSair}>
                Sair / Alterar Dados
              </button>
            )}
          </div>

          {videoAberto ? (
            <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
              <button 
                style={{ background: '#f8f9fa', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', color: '#495057' }} 
                onClick={() => setVideoAberto(null)}
              >
                ← Voltar para a lista
              </button>
              <h3 style={{ marginTop: 0, fontSize: '22px' }}>{videoAberto.titulo}</h3>
              <p style={{ color: '#6c757d', marginBottom: '20px' }}>{videoAberto.descricao}</p>
              
              {renderEmbed(videoAberto.url)}
              
              {/* --- SEÇÃO DE COMENTÁRIOS --- */}
              <div style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <h4 style={{ color: '#2c3e50', fontSize: '18px', marginBottom: '15px' }}>Comentários ({comentarios.length})</h4>
                
                <form onSubmit={handleAddComentario} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <input 
                    style={{ flex: 1, padding: '12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px' }} 
                    placeholder="Deixe um comentário, dúvida ou feedback..." 
                    value={novoComentario} 
                    onChange={e => setNovoComentario(e.target.value)} 
                    required 
                  />
                  <button type="submit" style={{ padding: '12px 20px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                    Enviar
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {comentarios.map(c => (
                    <div key={c.id} style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <strong style={{ color: '#34495e', fontSize: '15px' }}>{c.autor}</strong>
                        <span style={{ fontSize: '12px', color: '#95a5a6' }}>
                          {c.criadoEm && c.criadoEm.toDate ? c.criadoEm.toDate().toLocaleDateString('pt-BR') : ''}
                        </span>
                      </div>
                      <p style={{ margin: 0, color: '#555', fontSize: '15px', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>{c.texto}</p>
                    </div>
                  ))}
                  {comentarios.length === 0 && (
                    <p style={{ color: '#7f8c8d', fontStyle: 'italic', fontSize: '14px' }}>Nenhum comentário ainda. Seja o primeiro a comentar!</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
              {videos.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '12px', color: '#7f8c8d' }}>
                  Nenhum tutorial disponível no momento.
                </div>
              )}
              {videos.map(video => (
                <div key={video.id} style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer' }} onClick={() => assistVideo(video)}>
                  {/* Um placeholder de imagem se quisesse, por enquanto uma barra colorida */}
                  <div style={{ height: '140px', background: 'linear-gradient(135deg, #007bff 0%, #00d2ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '40px' }}>
                    ▶
                  </div>
                  <div style={{ padding: '20px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '18px', color: '#2c3e50' }}>{video.titulo}</h4>
                    <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '0 0 15px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {video.descricao || 'Sem descrição'}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ background: '#e9ecef', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', color: '#495057' }}>{video.duracao || 'Duração indisponível'}</span>
                      <span style={{ color: '#007bff', fontWeight: 'bold', fontSize: '14px' }}>Assistir</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default TutoriaisPublico;
